import React, { useEffect, useRef, useState } from 'react';

import { listAccrualRuns } from '../../services/api/admissionChargesApi';
import { formatDateTimeIST } from '../../utils/formatIST';

/**
 * AccrualRunBanner (C3 · FE-4, business non-negotiable) — a first-glance status
 * strip for the bed-day accrual job. Reads the latest JobRun row (C-4) and states
 * when bed charges last posted + how many were charged / failed, so a SILENT
 * accrual failure is impossible: `failed > 0` turns the strip amber.
 *
 * Mounted both above the admission desk list AND inside each running-bill panel.
 * Fetch is single-flight per mount (ref-guarded) — one request, never per render.
 * No run yet (empty list) → renders nothing (quiet pre-tick).
 */
export default function AccrualRunBanner() {
  const [run, setRun] = useState(undefined); // undefined = loading; null = none; obj = latest
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let alive = true;
    listAccrualRuns({ limit: 1 }).then((res) => {
      if (!alive) return;
      if (res.success) setRun(res.data.runs[0] || null);
      else setRun(null); // fail quiet — the desk still works without the strip
    });
    return () => { alive = false; };
  }, []);

  if (run === undefined || run === null) return null; // loading or no run yet → hidden

  const failed = Number(run.failed) || 0;
  const charged = Number(run.charged) || 0;
  const isRunning = run.status === 'RUNNING';
  const when = formatDateTimeIST(run.finished_at || run.started_at);

  const base = 'flex items-center gap-2 px-3.5 py-2 text-[12px] rounded-lg border';
  const tone = failed > 0
    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
    : 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/25 text-emerald-800 dark:text-emerald-200';
  const dot = failed > 0 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className={`${base} ${tone}`} data-testid="accrual-banner" role="status">
      <span className={`w-2 h-2 rounded-full flex-none ${dot}`} aria-hidden="true" />
      {isRunning ? (
        <span>Bed-day accrual is running…</span>
      ) : (
        <span data-testid="accrual-summary">
          Bed charges last posted {when} · <b>{charged}</b> charged · <b data-testid="accrual-failed">{failed}</b> failed
          {failed > 0 && ` — ${failed} bed charge${failed === 1 ? '' : 's'} couldn’t post. Review these bills before billing.`}
        </span>
      )}
    </div>
  );
}
